import React, { useState } from 'react';
import { Card, Select, DatePicker, Row, Col, Tag, Tabs, Progress, Table } from 'antd';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Users, Clock, AlertTriangle, Calendar, MapPin, Activity } from 'lucide-react';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const PeakHoursCrowdTrends = () => {
  const [location, setLocation] = useState('all');
  const [timeRange, setTimeRange] = useState('week');
  const [dateRange, setDateRange] = useState(null);

  const peakHoursData = [
    { hour: '6 AM', visitors: 45, capacity: 80 },
    { hour: '7 AM', visitors: 78, capacity: 80 },
    { hour: '8 AM', visitors: 123, capacity: 80 },
    { hour: '9 AM', visitors: 165, capacity: 80 },
    { hour: '10 AM', visitors: 198, capacity: 80 },
    { hour: '11 AM', visitors: 234, capacity: 80 },
    { hour: '12 PM', visitors: 289, capacity: 80 },
    { hour: '1 PM', visitors: 312, capacity: 80 },
    { hour: '2 PM', visitors: 276, capacity: 80 },
    { hour: '3 PM', visitors: 298, capacity: 80 },
    { hour: '4 PM', visitors: 265, capacity: 80 },
    { hour: '5 PM', visitors: 234, capacity: 80 },
    { hour: '6 PM', visitors: 198, capacity: 80 },
    { hour: '7 PM', visitors: 156, capacity: 80 },
    { hour: '8 PM', visitors: 123, capacity: 80 },
    { hour: '9 PM', visitors: 89, capacity: 80 }
  ];

  const weeklyTrendData = [
    { day: 'Monday', visitors: 2450, avgDuration: 35, peak: 312 },
    { day: 'Tuesday', visitors: 3120, avgDuration: 42, peak: 389 },
    { day: 'Wednesday', visitors: 2890, avgDuration: 38, peak: 356 },
    { day: 'Thursday', visitors: 3560, avgDuration: 45, peak: 423 },
    { day: 'Friday', visitors: 4230, avgDuration: 52, peak: 512 },
    { day: 'Saturday', visitors: 3870, avgDuration: 48, peak: 478 },
    { day: 'Sunday', visitors: 2980, avgDuration: 40, peak: 367 }
  ];

  const areaDistributionData = [
    { name: 'Main Entrance', value: 1200, percentage: 28 },
    { name: 'Lobby', value: 980, percentage: 23 },
    { name: 'Floor 1', value: 750, percentage: 17 },
    { name: 'Floor 2', value: 650, percentage: 15 },
    { name: 'Parking', value: 420, percentage: 10 },
    { name: 'Other', value: 300, percentage: 7 }
  ];

  const occupancyData = [
    { time: '6 AM', rate: 15 },
    { time: '8 AM', rate: 42 },
    { time: '10 AM', rate: 68 },
    { time: '12 PM', rate: 85 },
    { time: '2 PM', rate: 78 },
    { time: '4 PM', rate: 72 },
    { time: '6 PM', rate: 55 },
    { time: '8 PM', rate: 32 }
  ];

  const busiestPeriodsColumns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 70,
      render: (rank) => (
        <div className="flex items-center justify-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
            rank === 1 ? 'bg-yellow-100 text-yellow-700' :
            rank === 2 ? 'bg-gray-100 text-gray-700' :
            rank === 3 ? 'bg-orange-100 text-orange-700' :
            'bg-blue-50 text-blue-700'
          }`}>
            {rank}
          </div>
        </div>
      )
    },
    {
      title: 'Time Period',
      dataIndex: 'period',
      key: 'period',
    },
    {
      title: 'Day',
      dataIndex: 'day',
      key: 'day',
    },
    {
      title: 'Avg Visitors',
      dataIndex: 'visitors',
      key: 'visitors',
      render: (visitors) => (
        <span className="font-semibold text-blue-600">{visitors}</span>
      )
    },
    {
      title: 'Occupancy',
      dataIndex: 'occupancy',
      key: 'occupancy',
      render: (occupancy) => (
        <div className="flex items-center gap-2">
          <Progress 
            percent={occupancy} 
            size="small" 
            strokeColor={occupancy > 90 ? '#ef4444' : occupancy > 70 ? '#f59e0b' : '#10b981'}
            className="flex-1"
          />
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          'Very High': 'red',
          'High': 'orange',
          'Moderate': 'blue',
          'Normal': 'green'
        };
        return <Tag color={colors[status]}>{status}</Tag>;
      }
    }
  ];

  const busiestPeriodsData = [
    { key: 1, rank: 1, period: '12:00 PM - 2:00 PM', day: 'Friday', visitors: 512, occupancy: 95, status: 'Very High' },
    { key: 2, rank: 2, period: '1:00 PM - 3:00 PM', day: 'Saturday', visitors: 478, occupancy: 89, status: 'High' },
    { key: 3, rank: 3, period: '12:00 PM - 2:00 PM', day: 'Thursday', visitors: 423, occupancy: 82, status: 'High' },
    { key: 4, rank: 4, period: '11:00 AM - 1:00 PM', day: 'Tuesday', visitors: 389, occupancy: 76, status: 'Moderate' },
    { key: 5, rank: 5, period: '2:00 PM - 4:00 PM', day: 'Sunday', visitors: 367, occupancy: 71, status: 'Moderate' }
  ];

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#6b7280'];

  const locations = [
    { value: 'all', label: 'All Locations' },
    { value: 'entrance', label: 'Main Entrance' },
    { value: 'lobby', label: 'Lobby Area' },
    { value: 'floor1', label: 'Floor 1' },
    { value: 'floor2', label: 'Floor 2' }
  ];

  const timeRanges = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'custom', label: 'Custom Range' }
  ];

  return (
    <div>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Peak Hours & Crowd Trends</h1>
          <p className="text-gray-600">Analyze visitor patterns and crowd distribution</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Today's Visitors</p>
                <p className="text-3xl font-bold text-blue-600">3,847</p>
                <p className="text-xs text-green-600 mt-1">↑ 12% vs yesterday</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Peak Hour</p>
                <p className="text-3xl font-bold text-purple-600">1 PM</p>
                <p className="text-xs text-gray-600 mt-1">512 visitors</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </Card>

          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Occupancy Rate</p>
                <p className="text-3xl font-bold text-orange-600">78%</p>
                <p className="text-xs text-orange-600 mt-1">High traffic</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-full">
                <Activity className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </Card>

          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Avg Duration</p>
                <p className="text-3xl font-bold text-green-600">43m</p>
                <p className="text-xs text-gray-600 mt-1">Per visitor</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6 shadow-sm">
          <Row gutter={16}>
            <Col xs={24} sm={12} lg={8}>
              <div className="mb-4 lg:mb-0">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Location
                </label>
                <Select
                  className="w-full"
                  value={location}
                  onChange={setLocation}
                  options={locations}
                />
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <div className="mb-4 lg:mb-0">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Time Range
                </label>
                <Select
                  className="w-full"
                  value={timeRange}
                  onChange={setTimeRange}
                  options={timeRanges}
                />
              </div>
            </Col>
            <Col xs={24} lg={8}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Date Range
                </label>
                <RangePicker 
                  className="w-full"
                  onChange={(dates) => setDateRange(dates)}
                />
              </div>
            </Col>
          </Row>
        </Card>

        {/* Main Content Tabs */}
        <Tabs defaultActiveKey="1" className="mb-6">
          <TabPane tab="Peak Hours Analysis" key="1">
            <Row gutter={16}>
              {/* Peak Hours Chart */}
              <Col xs={24} lg={16}>
                <Card className="shadow-sm mb-4">
                  <div className="flex items-center mb-4">
                    <Clock className="w-5 h-5 text-purple-600 mr-2" />
                    <h2 className="text-lg font-semibold text-gray-800">Hourly Visitor Distribution</h2>
                  </div>
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={peakHoursData}>
                      <defs>
                        <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="hour" 
                        tick={{ fill: '#666', fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis tick={{ fill: '#666' }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="visitors" 
                        stroke="#8b5cf6" 
                        fillOpacity={1}
                        fill="url(#colorVisitors)"
                        name="Visitors"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card>

                {/* Occupancy Rate */}
                <Card className="shadow-sm">
                  <div className="flex items-center mb-4">
                    <Activity className="w-5 h-5 text-orange-600 mr-2" />
                    <h2 className="text-lg font-semibold text-gray-800">Occupancy Rate Timeline</h2>
                  </div>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={occupancyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="time" tick={{ fill: '#666' }} />
                      <YAxis tick={{ fill: '#666' }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px'
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="rate" 
                        stroke="#f59e0b" 
                        strokeWidth={3}
                        name="Occupancy %"
                        dot={{ fill: '#f59e0b', r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </Col>

              {/* Side Stats */}
              <Col xs={24} lg={8}>
                <Card className="shadow-sm mb-4">
                  <h3 className="font-semibold text-gray-800 mb-4">Peak Time Insights</h3>
                  <div className="space-y-4">
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Busiest Hour</p>
                      <p className="text-2xl font-bold text-purple-600">1:00 PM</p>
                      <p className="text-xs text-gray-600 mt-1">312 visitors</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Quietest Hour</p>
                      <p className="text-2xl font-bold text-green-600">6:00 AM</p>
                      <p className="text-xs text-gray-600 mt-1">45 visitors</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Average Peak</p>
                      <p className="text-2xl font-bold text-blue-600">287</p>
                      <p className="text-xs text-gray-600 mt-1">visitors/hour</p>
                    </div>
                  </div>
                </Card>

                <Card className="shadow-sm">
                  <h3 className="font-semibold text-gray-800 mb-4">Capacity Alerts</h3>
                  <div className="space-y-3">
                    <div className="flex items-start p-3 bg-red-50 border-l-4 border-red-500 rounded">
                      <AlertTriangle className="w-5 h-5 text-red-600 mr-2 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-red-800">High Traffic Alert</p>
                        <p className="text-xs text-red-700 mt-1">12:00 PM - 2:00 PM</p>
                      </div>
                    </div>
                    <div className="flex items-start p-3 bg-orange-50 border-l-4 border-orange-500 rounded">
                      <AlertTriangle className="w-5 h-5 text-orange-600 mr-2 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-orange-800">Moderate Traffic</p>
                        <p className="text-xs text-orange-700 mt-1">10:00 AM - 12:00 PM</p>
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="Crowd Trends" key="2">
            <Row gutter={16}>
              {/* Weekly Trends */}
              <Col xs={24} lg={16}>
                <Card className="shadow-sm mb-4">
                  <div className="flex items-center mb-4">
                    <TrendingUp className="w-5 h-5 text-blue-600 mr-2" />
                    <h2 className="text-lg font-semibold text-gray-800">Weekly Crowd Trends</h2>
                  </div>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={weeklyTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="day" tick={{ fill: '#666' }} />
                      <YAxis tick={{ fill: '#666' }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Bar dataKey="visitors" fill="#3b82f6" name="Total Visitors" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="peak" fill="#10b981" name="Peak Count" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>

                {/* Busiest Periods Table */}
                <Card className="shadow-sm">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Top 5 Busiest Periods</h2>
                  <Table 
                    columns={busiestPeriodsColumns} 
                    dataSource={busiestPeriodsData}
                    pagination={false}
                    size="middle"
                  />
                </Card>
              </Col>

              {/* Area Distribution */}
              <Col xs={24} lg={8}>
                <Card className="shadow-sm mb-4">
                  <div className="flex items-center mb-4">
                    <MapPin className="w-5 h-5 text-green-600 mr-2" />
                    <h2 className="text-lg font-semibold text-gray-800">Crowd Distribution</h2>
                  </div>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={areaDistributionData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percentage }) => `${name}: ${percentage}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {areaDistributionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>

                <Card className="shadow-sm">
                  <h3 className="font-semibold text-gray-800 mb-4">Area Details</h3>
                  <div className="space-y-3">
                    {areaDistributionData.map((area, index) => (
                      <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                        <div className="flex items-center">
                          <div 
                            className="w-3 h-3 rounded-full mr-2"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          ></div>
                          <span className="text-sm">{area.name}</span>
                        </div>
                        <span className="font-semibold text-sm">{area.value}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};

export default PeakHoursCrowdTrends;